import type { BookingDetailLineRequest } from 'api/types/booking.types';
import type { RatePlanOption } from 'api/types/availability.types';

// ============================================================================
// FORMATTERS
// ============================================================================

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

export const formatCurrency = (val?: number): string => {
  if (val == null) return '-';
  return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatTime = (timeStr?: string): string => {
  if (!timeStr) return '-';
  try {
    const parts = timeStr.split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${ampm}`;
  } catch { return timeStr; }
};

// ============================================================================
// STATUS HELPERS
// ============================================================================

export const getStatusBadgeClass = (statusCode: string): string => {
  switch (statusCode) {
    case 'TENTATIVE': return 'badge-warning';
    case 'CONFIRMED': return 'badge-active';
    case 'BLOCKED': return 'badge-warning';
    case 'PENDING_APPROVAL': return 'badge-info';
    case 'CHECKED_IN': return 'badge-primary';
    case 'CHECKED_OUT': return 'badge-secondary';
    case 'CANCELLED': return 'badge-inactive';
    case 'NO_SHOW': return 'badge-dark';
    default: return 'badge-secondary';
  }
};

// ============================================================================
// CALCULATIONS
// ============================================================================

/** Age-config-aware line type used by charge calculations */
export interface ExtraChargeLine extends BookingDetailLineRequest {
  baseOccupancy?: number;
  childAgeLimit?: number;
  chargeableChildAge?: number;
  infantAgeLimit?: number;
}

/**
 * Classify children by age against rate-plan age configuration.
 *
 * Returns:
 *  - childrenAsAdults: count with age >= childAgeLimit (charged at extraAdultRate)
 *  - chargeableChildren: count with chargeableChildAge <= age < childAgeLimit
 *  - freeChildren: infants / below chargeable age (no charge)
 *
 * Children without an age default to chargeable (conservative).
 */
export const classifyChildrenByAge = (
  childAges: (number | undefined)[],
  childAgeLimit: number,
  chargeableChildAge: number,
  infantAgeLimit: number,
): { childrenAsAdults: number; chargeableChildren: number; freeChildren: number } => {
  let childrenAsAdults = 0;
  let chargeableChildren = 0;
  let freeChildren = 0;
  for (const age of childAges) {
    if (age === undefined) {
      chargeableChildren++; // no age entered — assume chargeable
    } else if (age >= childAgeLimit) {
      childrenAsAdults++;
    } else if (age >= chargeableChildAge) {
      chargeableChildren++;
    } else {
      freeChildren++; // below chargeableChildAge (includes infants below infantAgeLimit)
    }
  }
  return { childrenAsAdults, chargeableChildren, freeChildren };
};

/**
 * Calculate extra charges based on occupancy beyond base allowance.
 *
 * - Extra adults  = adults beyond (baseOccupancy × rooms) + children counted as adults (age >= childAgeLimit)
 * - Chargeable children = children with chargeableChildAge <= age < childAgeLimit
 * - Infants / young children (age < chargeableChildAge) = FREE
 *
 * @param childAges  Optional array of ages from the guest list. When omitted, all
 *                   children in line.numChildren are treated as chargeable.
 */
export const calcExtraCharges = (
  line: ExtraChargeLine,
  numNights: number,
  childAges?: (number | undefined)[],
): number => {
  const baseOccupancy = line.baseOccupancy || 2;
  const rooms = line.numberOfRooms || 1;

  let extraAdults = Math.max(0, (line.numAdults || 0) - baseOccupancy * rooms);
  let chargeableChildren: number;

  if (childAges && childAges.length > 0) {
    const ageConfig = classifyChildrenByAge(
      childAges,
      line.childAgeLimit ?? 12,
      line.chargeableChildAge ?? 6,
      line.infantAgeLimit ?? 5,
    );
    extraAdults += ageConfig.childrenAsAdults;
    chargeableChildren = ageConfig.chargeableChildren;
  } else {
    chargeableChildren = line.numChildren || 0;
  }

  return (extraAdults * (line.extraAdultRate || 0) + chargeableChildren * (line.extraChildRate || 0)) * numNights;
};

/**
 * Calculate meal charges for ALL guests (not just extras).
 *
 * mealCharges = (numAdults × mealAdultRate + chargeableChildren × mealChildRate) × nights
 */
export const calcMealCharges = (
  line: ExtraChargeLine,
  numNights: number,
  childAges?: (number | undefined)[],
): number => {
  const mealAdultRate = line.mealAdultRate || 0;
  const mealChildRate = line.mealChildRate || 0;
  if (mealAdultRate === 0 && mealChildRate === 0) return 0;

  const numAdults = line.numAdults || 0;
  let chargeableChildren: number;

  if (childAges && childAges.length > 0) {
    const ageConfig = classifyChildrenByAge(
      childAges,
      line.childAgeLimit ?? 12,
      line.chargeableChildAge ?? 6,
      line.infantAgeLimit ?? 5,
    );
    chargeableChildren = ageConfig.chargeableChildren + ageConfig.childrenAsAdults;
  } else {
    chargeableChildren = line.numChildren || 0;
  }

  return (numAdults * mealAdultRate + chargeableChildren * mealChildRate) * numNights;
};

export const calcLineTotal = (line: ExtraChargeLine, numNights: number, childAges?: (number | undefined)[]): number => {
  const roomCharges = (line.ratePerRoom || 0) * (line.numberOfRooms || 1) * numNights;
  return roomCharges + calcExtraCharges(line, numNights, childAges) + calcMealCharges(line, numNights, childAges);
};

// ----------------------------------------------------------------------------
// BUNDLED PRICING — mirrors BundledStrategy.cs
// ----------------------------------------------------------------------------

/** Bundled-mode charge buckets for one line, per line, across the full stay. */
export interface BundledLineCharges {
  roomCharges: number;
  mealCharges: number;   // bundle + diff (base-occupancy meals)
  extraCharges: number;  // extras (room + selected meal for pax beyond base)
  lineTotal: number;
  /**
   * Quantities each adjustable unit rate was billed on. Mirrors LineCharges.*Units
   * on the server — the rate-adjustment panel multiplies its delta by these, so if
   * they drift from the server's the screen and the saved booking disagree.
   *
   * Under bundled, mealAdultUnits is (base_capacity + extra_adults) x nights, NOT
   * actual adults: the bundle is billed on every base slot whether occupied or not.
   * Deriving it from actual adults made a discount grow with occupancy while the
   * charge stood still, so the booking priced lower the fuller it got.
   */
  roomUnits: number;
  mealAdultUnits: number;
  extraAdultUnits: number;
}

/**
 * Bundled pricing — flat room+default-meal bundle for base_occupancy + per-pax
 * diff on meal switch + extras with full selected meal rate.
 *
 * Requires the line to carry `defaultMealAdultRate` / `defaultMealChildRate`
 * alongside the usual `mealAdultRate` / `mealChildRate` (selected plan).
 * When those defaults are 0, the bundle still works (diff = selected rate).
 *
 * Mirrors crs.Core/Services/BookingModule/Pricing/BundledStrategy.cs. Keep in
 * sync with any server-side formula change.
 */
export const calcBundledLine = (
  line: ExtraChargeLine,
  numNights: number,
  childAges?: (number | undefined)[],
): BundledLineCharges => {
  const baseOccupancy = line.baseOccupancy || 2;
  const rooms = line.numberOfRooms || 1;
  const baseCapacity = baseOccupancy * rooms;

  const roomCharges = (line.ratePerRoom || 0) * rooms * numNights;

  // Classify pax using the shared logic (infants free, children-aged-as-adults
  // rolled up for allocation).
  let totalAdults = line.numAdults || 0;
  let chargeableChildren: number;

  if (childAges && childAges.length > 0) {
    const ageConfig = classifyChildrenByAge(
      childAges,
      line.childAgeLimit ?? 12,
      line.chargeableChildAge ?? 6,
      line.infantAgeLimit ?? 5,
    );
    totalAdults += ageConfig.childrenAsAdults;
    chargeableChildren = ageConfig.chargeableChildren;
  } else {
    chargeableChildren = line.numChildren || 0;
  }

  // Split pax into base-occupancy slots vs extras. Adults first, then children.
  const baseAdults = Math.min(totalAdults, baseCapacity);
  const baseChildren = Math.min(chargeableChildren, Math.max(0, baseCapacity - baseAdults));
  const extraAdults = Math.max(0, totalAdults - baseCapacity);
  const extraChildren = Math.max(0, chargeableChildren - baseChildren);

  const defaultAdult = line.defaultMealAdultRate ?? 0;
  const defaultChild = line.defaultMealChildRate ?? 0;
  const selectedAdult = line.mealAdultRate || 0;
  const selectedChild = line.mealChildRate || 0;

  // Meal bundle — flat, uses DEFAULT adult rate. Independent of pax composition.
  const mealBundle = defaultAdult * baseCapacity;

  // Upgrade diff for pax in base slots. Zero when selected == default.
  // Clamped at zero on downgrade — the bundle headline price is the contractual
  // floor; selecting a cheaper meal plan does NOT refund the bundle.
  const mealDiff = Math.max(
    0,
    baseAdults * (selectedAdult - defaultAdult)
      + baseChildren * (selectedChild - defaultChild)
  );

  // Split extras into room + meal so the summary buckets stay honest.
  const extrasRoom =
      extraAdults   * (line.extraAdultRate || 0)
    + extraChildren * (line.extraChildRate || 0);
  const extrasMeal =
      extraAdults   * selectedAdult
    + extraChildren * selectedChild;

  const mealCharges = (mealBundle + mealDiff + extrasMeal) * numNights;
  const extraCharges = extrasRoom * numNights;

  return {
    roomCharges,
    mealCharges,
    extraCharges,
    lineTotal: roomCharges + mealCharges + extraCharges,
    roomUnits: rooms * numNights,
    // Base slots are charged whether or not somebody sleeps in them; extra adults
    // pay the same meal rate on top, so an agreed rate reaches them too.
    mealAdultUnits: (baseCapacity + extraAdults) * numNights,
    extraAdultUnits: extraAdults * numNights,
  };
};

/**
 * Dispatches to the correct pricing calculator based on the property-level
 * pricing model. Defaults to a_la_carte for anything unrecognized (matches
 * the backend normalization rule).
 */
/** Matches the backend's `decimal.Round(x, 2)` so both sides agree to the paisa. */
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Group / package pricing — mirrors the backend `PerPaxStrategy` exactly.
 *
 * The line total is what the invoice quotes: pax × rate × nights. The meal-plan
 * value is deducted FIRST and the balance becomes room revenue, so F&B revenue
 * and kitchen costing stay exact while the room portion absorbs any rounding.
 * Extra-person charges are suppressed — the per-pax rate already IS the
 * per-person charge, so stacking extras on top would double-charge.
 *
 * Keep in step with crs-api `PerPaxStrategy.CalculateLine`: if the two diverge,
 * the figure the agent agreed with the client stops matching what is stored.
 */
export const calcPerPaxLine = (
  line: ExtraChargeLine,
  numNights: number,
): BundledLineCharges => {
  const pax = (line.numAdults || 0) + (line.numChildren || 0);
  const packageValue = round2((line.ratePerPax || 0) * pax * numNights);

  let mealCharges = round2(
    (((line.mealAdultRate || 0) * (line.numAdults || 0))
      + ((line.mealChildRate || 0) * (line.numChildren || 0))) * numNights,
  );
  let roomCharges = round2(packageValue - mealCharges);

  // A negative room tariff would silently corrupt ARR and RevPAR.
  if (roomCharges < 0) {
    mealCharges = packageValue;
    roomCharges = 0;
  }

  // roomUnits stays 0: the room portion is a BALANCE (package minus meal), not a
  // rate times a quantity, so there is no per-room-night rate to adjust against.
  return {
    roomCharges, mealCharges, extraCharges: 0, lineTotal: packageValue,
    roomUnits: 0,
    mealAdultUnits: (line.numAdults || 0) * numNights,
    extraAdultUnits: 0,
  };
};

export const calcLineByModel = (
  pricingModel: string | undefined,
  line: ExtraChargeLine,
  numNights: number,
  childAges?: (number | undefined)[],
): BundledLineCharges => {
  // Per-pax overrides the property model: the basis is a property OF THE LINE,
  // so a group line prices per pax even inside an a_la_carte property.
  if (line.rateBasis === 'PER_PAX') {
    return calcPerPaxLine(line, numNights);
  }

  const isBundled = (pricingModel?.trim().toLowerCase() === 'bundled');

  if (isBundled) {
    return calcBundledLine(line, numNights, childAges);
  }

  // a_la_carte (legacy): 3 separate calcs summed.
  const rooms = line.numberOfRooms || 1;
  const roomCharges = (line.ratePerRoom || 0) * rooms * numNights;
  const extraCharges = calcExtraCharges(line, numNights, childAges);
  const mealCharges = calcMealCharges(line, numNights, childAges);

  // Extra ADULTS only — mirrors ALaCarteStrategy. Chargeable children share the
  // extras money at extraChildRate, so folding them into an adult quantity (which
  // dividing the bucket by extraAdultRate silently does) describes neither rate.
  const ageCfg = childAges && childAges.length > 0
    ? classifyChildrenByAge(
        childAges,
        line.childAgeLimit ?? 12,
        line.chargeableChildAge ?? 6,
        line.infantAgeLimit ?? 5,
      )
    : null;
  const extraAdults = Math.max(0, (line.numAdults || 0) - (line.baseOccupancy || 2) * rooms)
    + (ageCfg?.childrenAsAdults ?? 0);
  const mealsCharged = (line.mealAdultRate || 0) !== 0 || (line.mealChildRate || 0) !== 0;

  return {
    roomCharges,
    mealCharges,
    extraCharges,
    lineTotal: roomCharges + mealCharges + extraCharges,
    roomUnits: rooms * numNights,
    // Meals here genuinely DO scale with actual adults, so the charge and the
    // discount move together and occupancy raises the price as it should.
    mealAdultUnits: mealsCharged ? (line.numAdults || 0) * numNights : 0,
    extraAdultUnits: extraAdults * numNights,
  };
};

// TAX_RATE removed — tax calculation deferred until a Tax Configuration module is built.
// Rate plans now carry an includeTaxes flag (tax-inclusive vs tax-exclusive).

// ============================================================================
// BOOKING SOURCE CONFIGURATION
// ============================================================================

/**
 * The values stored in booking.tbl_booking.booking_source. Branch on these
 * constants, never on a literal — a reworded label would otherwise silently
 * stop a source-conditional rule from firing, with no error.
 *
 * These are the DISPLAY VALUES (`enum_value` of `BOOKING_SOURCE` in
 * common.tbl_enum_master), which is the CRS convention for name-based columns —
 * see the note on `EnumItem.enumValue` and how CheckInGuestsPanel persists
 * `enumValue` for guest ID types. Switching the column to `enum_key` is a
 * separate optional change (crs-doc/CRS-Booking-Source-Vocabulary-Fix-Design.md
 * §4); when it happens only the values here and in BookingSources.cs change.
 *
 * This is NOT the list of selectable sources. That comes from the enum at
 * runtime via `useBookingSources()`, so adding a source stays an enum-only
 * change. What lives here is only what the UI BRANCHES on.
 *
 * C# twin: crs.Core/DTOs/BookingModule/BookingSources.cs — keep in step.
 */
export const BOOKING_SOURCE = {
  DIRECT:       'Direct',
  WALK_IN:      'Walk-In',
  TRAVEL_AGENT: 'Travel Agent',
  OTA:          'OTA',
  CORPORATE:    'Corporate',
  // Retired (INACTIVE) by migrations/fixes/033 — not selectable for new
  // bookings. Kept because this object describes values the COLUMN may hold,
  // not the selectable list: a historical booking carrying one must still
  // resolve through isGuestNameRequired and the party-label helpers.
  WEBSITE:      'Website',
  GOVERNMENT:   'Government',
  COMPLIMENTARY: 'Complimentary',
} as const;

/** The enum group in common.tbl_enum_master that owns this vocabulary. */
export const BOOKING_SOURCE_ENUM_GROUP = 'BOOKING_SOURCE';

/**
 * Sources for which a primary guest name is mandatory — the party IS the guest.
 * Mirrors `BookingService.GuestNameRequiredSources`. Sources absent from this
 * set (including any added later via the enum) default to not required, which
 * is the safe direction: the server is the enforcement point either way.
 */
const GUEST_NAME_REQUIRED_SOURCES: readonly string[] = [
  BOOKING_SOURCE.DIRECT,
  BOOKING_SOURCE.WALK_IN,
  BOOKING_SOURCE.WEBSITE,
];

export const isGuestNameRequired = (source: string): boolean =>
  GUEST_NAME_REQUIRED_SOURCES.includes(source);

// ============================================================================
// AVAILABILITY BADGE
// ============================================================================

export const getAvailBadgeClass = (available: number, total: number): string => {
  if (total === 0) return 'zero';
  const pct = (available / total) * 100;
  if (pct >= 80) return 'high';
  if (pct >= 40) return 'medium';
  if (pct > 0) return 'low';
  return 'zero';
};

/**
 * Color-band classifier driven by **occupancy** (booked / total). Used by the
 * availability calendar so cell colors reflect how full each day is.
 *
 *   0%        : empty       → 'high'   (green)
 *   1–60%     : low–medium  → 'medium' (yellow)
 *   61–84%    : busy        → 'low'    (orange-red)
 *   85–99%    : near full   → 'critical' (deep red)
 *   100%      : sold out    → 'zero'   (gray, struck through)
 *
 * Defined in terms of occupancy (not availability) so the legend and band names
 * read naturally — high-band color = good for booking, deep-red = almost full.
 */
export const getOccupancyBadgeClass = (booked: number, total: number): string => {
  if (total === 0) return 'zero';
  const occPct = (booked / total) * 100;
  if (occPct >= 100) return 'zero';
  if (occPct >= 85) return 'critical';
  if (occPct >= 61) return 'low';
  if (occPct >= 1)  return 'medium';
  return 'high';
};

// ============================================================================
// RATE PLAN HELPERS
// ============================================================================

/** Find the rate plan with the lowest ratePerRoom for a category. Prefers default meal plan among ties. */
export const findBestRatePlan = (ratePlans: RatePlanOption[]): RatePlanOption | null => {
  if (!ratePlans?.length) return null;
  return ratePlans.reduce((best, rp) => {
    if (rp.ratePerRoom < best.ratePerRoom) return rp;
    if (rp.ratePerRoom === best.ratePerRoom && rp.isDefaultMealPlan && !best.isDefaultMealPlan) return rp;
    return best;
  });
};

// ============================================================================
// CALENDAR DATE UTILITIES
// ============================================================================

/** Add N days to a date (returns new Date) */
export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/** Format a Date to YYYY-MM-DD using local timezone (NOT UTC) */
export const toLocalDateStr = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/** Generate an array of YYYY-MM-DD strings between start and end (inclusive) */
export const getDatesBetween = (start: Date, end: Date): string[] => {
  const dates: string[] = [];
  const d = new Date(start);
  while (d <= end) {
    dates.push(toLocalDateStr(d));
    d.setDate(d.getDate() + 1);
  }
  return dates;
};

// ============================================================================
// GROUP ALLOCATION (CRS-Group-Booking-Entry-Design.md phase B)
// ============================================================================

/** One category the allocator may draw from. */
export interface AllocatableCategory {
  roomCategoryId: string;
  categoryName: string;
  availableRooms: number;
  baseOccupancy: number;
  maxOccupancy: number;
  displayOrder?: number;
}

export interface ProposedAllocationRow {
  roomCategoryId: string;
  categoryName: string;
  rooms: number;
  pax: number;
  /** How this row relates to the category the operator asked for. */
  kind: 'preferred' | 'upgrade' | 'downgrade' | 'other';
}

export interface AllocationProposal {
  rows: ProposedAllocationRow[];
  /** Rooms that could not be placed anywhere. Reported, never silently dropped. */
  shortfall: number;
  /** Total free rooms across every category — the honest ceiling for the message. */
  totalAvailable: number;
}

/**
 * Spreads a room requirement across categories.
 *
 * Ordering is **upgrade-first by display-order adjacency**: the preferred category,
 * then the nearest categories, preferring a better room over a worse one at equal
 * distance. Cheapest-first would optimise the property's cost but routinely propose
 * downgrades the desk then has to unpick with the guest.
 *
 * `availableRooms` is already the minimum across every night of the stay
 * (`AvailabilityService` collapses it with `Min(d => d.AvailableRooms)`), so a
 * category with ten rooms free tonight and three on Thursday contributes three.
 * No proposed row can over-commit a night.
 *
 * @param alreadyAllocated rooms per category already on the booking — the caller
 *        passes this so re-running proposes only the DELTA rather than a fresh
 *        allocation that would fight the operator's existing lines.
 */
export const proposeAllocation = (
  requiredRooms: number,
  preferredCategoryId: string | undefined,
  categories: AllocatableCategory[],
  alreadyAllocated: Record<string, number> = {},
): AllocationProposal => {
  const usedSoFar = Object.values(alreadyAllocated).reduce((a, b) => a + b, 0);
  let remaining = Math.max(0, requiredRooms - usedSoFar);

  const freeIn = (c: AllocatableCategory) =>
    Math.max(0, c.availableRooms - (alreadyAllocated[c.roomCategoryId] ?? 0));

  const totalAvailable = categories.reduce((sum, c) => sum + freeIn(c), 0);

  const preferred = categories.find(c => c.roomCategoryId === preferredCategoryId);
  const preferredOrder = preferred?.displayOrder;

  const ranked = [...categories].sort((a, b) => {
    if (a.roomCategoryId === preferredCategoryId) return -1;
    if (b.roomCategoryId === preferredCategoryId) return 1;

    // Without a display order on either side there is nothing to rank by, so fall
    // back to the property's own listing rather than inventing a hierarchy.
    if (preferredOrder == null || a.displayOrder == null || b.displayOrder == null) {
      return (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999);
    }

    const da = Math.abs(a.displayOrder - preferredOrder);
    const db = Math.abs(b.displayOrder - preferredOrder);
    if (da !== db) return da - db;

    // Equal distance — take the upgrade. A guest accepts a better room; a
    // downgrade needs a conversation.
    return b.displayOrder - a.displayOrder;
  });

  const classify = (c: AllocatableCategory): ProposedAllocationRow['kind'] => {
    if (c.roomCategoryId === preferredCategoryId) return 'preferred';
    if (preferredOrder == null || c.displayOrder == null || c.displayOrder === preferredOrder) {
      return 'other';
    }
    return c.displayOrder > preferredOrder ? 'upgrade' : 'downgrade';
  };

  const rows: ProposedAllocationRow[] = [];
  for (const c of ranked) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, freeIn(c));
    if (take <= 0) continue;

    // baseOccupancy is how the room is normally SOLD; maxOccupancy is the
    // fire-safety ceiling. Deriving from max would under-allocate every group.
    const pax = Math.min(take * c.baseOccupancy, take * c.maxOccupancy);

    rows.push({
      roomCategoryId: c.roomCategoryId,
      categoryName: c.categoryName,
      rooms: take,
      pax,
      kind: classify(c),
    });
    remaining -= take;
  }

  return { rows, shortfall: remaining, totalAvailable };
};
